/**
 * GlobalToast — WCAG 4.1.3 (Status Messages)
 *
 * Listens for 'academicos-toast' CustomEvents and renders accessible
 * Bootstrap Toast notifications. Mount once in App.js.
 *
 * Screen readers announce toasts via aria-live="polite" (info/success)
 * or aria-live="assertive" (error/warning). The role="status" or
 * role="alert" attribute is set accordingly.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Toast, ToastContainer } from 'react-bootstrap';

const ICONS = {
    success: '✓',
    error:   '✕',
    warning: '⚠',
    info:    'ℹ',
};

const ARIA_ROLES = {
    success: 'status',
    error:   'alert',
    warning: 'alert',
    info:    'status',
};

const BG_MAP = {
    success: 'success',
    error:   'danger',
    warning: 'warning',
    info:    'info',
};

let _id = 0;

export default function GlobalToast() {
    const [toasts, setToasts] = useState([]);

    const addToast = useCallback((e) => {
        const { message, variant = 'info' } = e.detail || {};
        if (!message) return;
        const id = ++_id;
        setToasts(prev => [...prev, { id, message, variant }]);
        // Auto-dismiss: errors stay 8 s, others 5 s
        const ttl = (variant === 'error' || variant === 'warning') ? 8000 : 5000;
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, ttl);
    }, []);

    useEffect(() => {
        window.addEventListener('academicos-toast', addToast);
        return () => window.removeEventListener('academicos-toast', addToast);
    }, [addToast]);

    if (toasts.length === 0) return null;

    return (
        <ToastContainer
            position="bottom-end"
            className="p-3"
            style={{ zIndex: 9990 }}
            aria-label="Notifications"
        >
            {toasts.map(({ id, message, variant }) => (
                <Toast
                    key={id}
                    bg={BG_MAP[variant] || 'info'}
                    onClose={() => setToasts(prev => prev.filter(t => t.id !== id))}
                    role={ARIA_ROLES[variant] || 'status'}
                    aria-live={variant === 'error' || variant === 'warning' ? 'assertive' : 'polite'}
                    aria-atomic="true"
                    className="text-white"
                >
                    <Toast.Header closeButton closeVariant="white" className={`bg-${BG_MAP[variant]} text-white`}>
                        <span className="me-2" aria-hidden="true">{ICONS[variant]}</span>
                        <strong className="me-auto" style={{ textTransform: 'capitalize' }}>{variant}</strong>
                    </Toast.Header>
                    <Toast.Body>{message}</Toast.Body>
                </Toast>
            ))}
        </ToastContainer>
    );
}
